import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { message } from 'antd';
import {
  CreativityEditor,
  type CreativityEditorHandle,
  type ResolutionConfig,
  type ExportResult
} from '@doer/frontend-components/creativity-editor';
import { useAvatarsProject, type AvatarsProject } from './hooks/use-avatars-project';
import { useSaveAvatarContent } from './hooks/use-save-avatar-content';
import { useAvatarsOutroPlaca } from './hooks/use-avatars-outro-placa';
import { useAvatarsMusicTrack } from './hooks/use-avatars-music-track';
import { useAvatarsPieceModel } from './hooks/use-avatars-piece-model';
import { useUploadMedia } from '../../hooks/media/use-upload-media';
import { useEditorUpload } from '../../hooks/use-editor-upload';
import { useClientId } from '../../hooks/use-client-id';
import { useContentReady } from '../../hooks/use-content-ready';
import { LoadingScreen } from '@doer/frontend-components/spinner';
import { addCacheBusting } from '../../utils/cache-busting';
import { dehydrateSceneUrls } from './utils/scene-url-utils';
import { BannerMediaFiles } from '../../../../types/brands/banner-media';
import { HeyGenDimension } from '../../types/avatar-video';
import { AppHeader } from '../../components/ui/layout/app-layout';
import { BrandsAudioServicesProvider } from '../creativity-editor';
import { getBrandKit } from 'src/clients/brand-kit-registry';
import { getAvatarsSubtitleStyle } from 'src/clients/avatars-subtitles-registry';

export const AvatarsEditorPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project, isLoading, error } = useAvatarsProject(projectId);

  return (
    <BrandsAudioServicesProvider>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <AppHeader />
        {error ? (
          <div style={{ padding: 24 }}>Error: {error.message}</div>
        ) : isLoading || !project ? (
          <LoadingScreen label='Cargando pieza…' />
        ) : (
          <AvatarsEditorReady project={project} />
        )}
      </div>
    </BrandsAudioServicesProvider>
  );
};

const AvatarsEditorReady: React.FC<{ project: AvatarsProject }> = ({ project }) => {
  const navigate = useNavigate();
  const editorRef = useRef<CreativityEditorHandle>(null);
  const { saveAvatarContent } = useSaveAvatarContent();
  const { uploadVideoDirect } = useUploadMedia();
  const uploadMedia = useEditorUpload('avatars');
  const clientId = useClientId();
  const [isSaving, setIsSaving] = useState(false);

  const [editorReady, setEditorReady] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const onEditorReady = useCallback(() => setEditorReady(true), []);
  const contentReady = useContentReady(editorReady, mediaLoading);

  // Solo el path legacy `editorState` respeta la pieza guardada tal cual (los
  // hooks devuelven null y no re-inyectan).
  const skipInjection = project.editorState !== null;

  // Resuelve la placa de cierre. En el path editorModel (smart merge) siempre
  // se resuelve para poder inyectarla; en editorState (legacy) se saltea.
  const { outro, isReady: placaReady } = useAvatarsOutroPlaca({
    clientId,
    dimension: project.dimension as HeyGenDimension,
    placaId: project.placaId,
    hasSavedPiece: skipInjection
  });

  // Track de música pre-seleccionado. Mismo criterio que la placa.
  const { track: music, isReady: musicReady } = useAvatarsMusicTrack({
    musicTrackId: project.musicTrackId,
    hasSavedPiece: skipInjection
  });
  const brandKit = React.useMemo(() => getBrandKit(clientId), [clientId]);
  const subtitleStyle = React.useMemo(() => getAvatarsSubtitleStyle(clientId), [clientId]);

  const musicTrack = useMemo(
    () => (music ? { trackId: music.trackId, trackName: music.trackName, audioUrl: music.audioUrl } : undefined),
    [music]
  );

  const pieceModel = useAvatarsPieceModel({
    project,
    inputsReady: placaReady && musicReady,
    outro: outro ?? undefined,
    music: musicTrack,
    subtitleStyle
  });

  const resolutions: ResolutionConfig[] = useMemo(
    () => [
      {
        id: 'main',
        label: `${project.dimension.width}×${project.dimension.height}`,
        canvasWidth: project.dimension.width,
        canvasHeight: project.dimension.height,
        type: 'video',
        layout: { version: '6.0.0', objects: [] }
      }
    ],
    [project.dimension]
  );

  /** Sube un blob de video a S3 y devuelve el mediaFiles para persistir en DB. */
  const uploadVideoBlob = useCallback(
    async (blob: Blob): Promise<BannerMediaFiles[]> => {
      const file = new File([blob], `avatar-${project.projectId}.mp4`, { type: blob.type });
      const { publicUrl } = await uploadVideoDirect(
        'video',
        '.mp4',
        file,
        clientId,
        project.projectId,
        'export',
        false
      );
      const thumbnail = project.scenes[0]?.thumbnailUrl || undefined;
      return [{ type: 'video', url: addCacheBusting(publicUrl, new Date()), thumbnail }];
    },
    [project.projectId, project.scenes, clientId, uploadVideoDirect]
  );

  // Volver al Studio en edit mode. `avatars-page` consume el query param al mount
  // y dispara loadProjectForEdit; después limpia la URL.
  const handleBack = useCallback(() => {
    navigate(`/avatar-video?projectId=${project.projectId}`);
  }, [navigate, project.projectId]);

  const handleSave = useCallback(async () => {
    const model = editorRef.current?.getModel();
    if (!model) return;

    setIsSaving(true);
    const posterUrl = project.scenes[0]?.thumbnailUrl || undefined;
    let mediaFiles: BannerMediaFiles[] | undefined;

    try {
      const blob = await editorRef.current?.recordVideo();
      if (blob) {
        mediaFiles = await uploadVideoBlob(blob);
      }
    } catch {
      // recordVideo rechazado (cancelado o sin contenido temporal) — se guarda solo el modelo
    }

    try {
      const dehydrated = dehydrateSceneUrls(model);
      await saveAvatarContent(project.projectId, JSON.stringify(dehydrated), posterUrl, mediaFiles);
      message.success('Proyecto guardado');
    } catch {
      message.error('Error al guardar el proyecto');
    } finally {
      setIsSaving(false);
    }
  }, [project.projectId, project.scenes, uploadVideoBlob, saveAvatarContent]);

  /** Callback post-export: sube el video a S3 y actualiza mediaFiles en DB. */
  const handleExportComplete = useCallback(
    async (result: ExportResult) => {
      const model = editorRef.current?.getModel();
      if (!model) return;

      const posterUrl = project.scenes[0]?.thumbnailUrl || undefined;
      try {
        const mediaFiles = await uploadVideoBlob(result.blob);
        const dehydrated = dehydrateSceneUrls(model);
        await saveAvatarContent(project.projectId, JSON.stringify(dehydrated), posterUrl, mediaFiles);
      } catch {
        message.error('Error al guardar el video exportado');
      }
    },
    [project.projectId, project.scenes, uploadVideoBlob, saveAvatarContent]
  );

  // Gateamos el render del editor hasta terminar los probes de placa y música, y hasta
  // tener la pieza: el editor la carga una sola vez, así que tiene que estar lista.
  if (!placaReady || !musicReady || !pieceModel) {
    return <LoadingScreen label='Cargando pieza…' />;
  }

  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <CreativityEditor
        ref={editorRef}
        host='avatars'
        brandKit={brandKit}
        initialPieceModel={pieceModel}
        resolutions={resolutions}
        onReady={onEditorReady}
        onLoadingChange={setMediaLoading}
        onSave={handleSave}
        onExport={handleExportComplete}
        onBack={handleBack}
        isSaving={isSaving}
        uploadMedia={uploadMedia}
      />

      {/* Los videos tardan en decodificar: tapamos el canvas a medio dibujar. */}
      {!contentReady && <LoadingScreen label='Cargando pieza…' overlay />}
    </div>
  );
};
